import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, interval, EMPTY, Subject } from 'rxjs';
import { tap, switchMap, catchError, filter, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NotificationsApiService, Notification } from './api/notifications-api.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BtnUploadInfoDialogComponent,
  BtnUploadInfoDialogData,
} from '../../map-modules/external-entities/components/layers-list/btn-upload-info-dialog.component';

interface NotificationsState {
  notifications: Notification[];
  shownNotificationIds: Set<string>;
  projectId: string | null;
  isPolling: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService extends ComponentStore<NotificationsState> {
  readonly notifications$ = this.select((state) => state.notifications);
  readonly unreadCount$ = this.select((state) => state.notifications.filter((n) => n.unread).length);
  readonly projectId$ = this.select((state) => state.projectId);
  readonly isPolling$ = this.select((state) => state.isPolling);

  private stopPolling$ = new Subject<void>();
  private dialog = inject(MatDialog);
  private onOpenBtnTableCallback?: (fileId: string) => void;

  constructor(
    private notificationsApi: NotificationsApiService,
    private message: NzMessageService,
    private translate: TranslateService,
  ) {
    super({
      notifications: [],
      shownNotificationIds: new Set(),
      projectId: null,
      isPolling: false,
    });
  }

  setOnOpenBtnTableCallback(callback: (fileId: string) => void): void {
    this.onOpenBtnTableCallback = callback;
  }

  readonly setProjectId = this.updater((state, projectId: string | null) => ({
    ...state,
    projectId,
  }));

  readonly setNotifications = this.updater((state, notifications: Notification[]) => ({
    ...state,
    notifications,
  }));

  readonly markNotificationAsShown = this.updater((state, notificationId: string) => {
    const newShownIds = new Set(state.shownNotificationIds);
    newShownIds.add(notificationId);
    return {
      ...state,
      shownNotificationIds: newShownIds,
    };
  });

  readonly setIsPolling = this.updater((state, isPolling: boolean) => ({
    ...state,
    isPolling,
  }));

  readonly fetchUnreadNotifications = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() => {
        const projectId = this.get().projectId;
        if (!projectId) return EMPTY;

        return this.notificationsApi.getUnreadNotifications(projectId).pipe(
          tap((notifications) => {
            this.setNotifications(notifications);
            this.processNewNotifications(notifications);
          }),
          catchError((error) => {
            console.error('Error fetching notifications:', error);
            if (error.status === 403) {
              this.stopPolling();
            }
            return EMPTY;
          }),
        );
      }),
    );
  });

  readonly markAsRead = this.effect((notificationId$: Observable<string>) => {
    return notificationId$.pipe(
      switchMap((notificationId) => {
        const projectId = this.get().projectId;
        if (!projectId) return EMPTY;

        return this.notificationsApi.markAsRead(projectId, notificationId).pipe(
          tap(() => {
            const notifications = this.get().notifications.map((n) =>
              n.id === notificationId ? { ...n, unread: false } : n,
            );
            this.setNotifications(notifications);
          }),
          catchError((error) => {
            console.error('Error marking notification as read:', error);
            return EMPTY;
          }),
        );
      }),
    );
  });

  readonly startPolling = this.effect((projectId$: Observable<string>) => {
    return projectId$.pipe(
      distinctUntilChanged(),
      filter((projectId) => !!projectId),
      tap((projectId) => {
        this.stopPolling$.next();
        this.setProjectId(projectId);
        this.setIsPolling(true);
        this.fetchUnreadNotifications();
      }),
      switchMap(() => {
        return interval(10000).pipe(
          tap(() => this.fetchUnreadNotifications()),
          takeUntil(this.stopPolling$),
        );
      }),
    );
  });

  stopPolling(): void {
    this.stopPolling$.next();
    this.setIsPolling(false);
    this.setProjectId(null);
  }

  private processNewNotifications(notifications: Notification[]): void {
    const state = this.get();

    notifications.forEach((notification) => {
      if (state.shownNotificationIds.has(notification.id)) {
        return;
      }

      this.showNotification(notification);
      this.markNotificationAsShown(notification.id);
      this.markAsRead(notification.id);
    });
  }

  private showNotification(notification: Notification): void {
    const { type, message, file } = notification;

    // DEBUG: Log notification details
    console.log('📬 Notification received:', {
      type,
      message,
      file,
      hasFile: !!file,
      fullNotification: notification
    });

    if (type === 'btn_processing_completed' && file) {
      const dialogData: BtnUploadInfoDialogData = {
        id: file.id,
        name: file.originalName,
        handled: 0,
        total: 0,
        onOpenTable: this.onOpenBtnTableCallback || (() => {}),
        status: 'done',
        simpleMode: true, // Use simple mode for notifications
      };

      this.dialog.open(BtnUploadInfoDialogComponent, {
        data: dialogData,
        disableClose: false,
        autoFocus: true,
        width: 'auto',
        maxWidth: '90vw',
      });
    } else if (type === 'btn_processing_stopped_no_credits') {
      // Show warning message for no credits
      const translatedMessage =
        this.translate.instant('notification_btn_no_credits') ||
        'BTN file processing stopped: insufficient credits';
      this.message.warning(translatedMessage, { nzDuration: 0 });
    } else {
      this.message.info(message, { nzDuration: 5000 });
    }
  }
}

